// src/contexts/OnboardingContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useEmployees } from './EmployeeContext';
import { useNotifications } from './NotificationContext';
import { ONBOARDING_CHECKLIST, getAllChecklistItems, getRequiredItemsCount, getCompletedItemsCount } from './OnboardingChecklistConfig';
import onboardingService from '../services/onboarding.service';

const OnboardingContext = createContext();

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};

// Removed localStorage - all data comes from backend only

// Required documents for onboarding
export const REQUIRED_DOCUMENTS = [
    { id: 'pan_card', name: 'PAN Card', required: true },
    { id: 'aadhar', name: 'Aadhar Card', required: true },
    { id: 'passport', name: 'Passport', required: false },
    { id: 'resume', name: 'Resume/CV', required: true },
    { id: 'education_certificate', name: 'Education Certificate', required: true },
    { id: 'previous_employment', name: 'Previous Employment Documents', required: false },
    { id: 'bank_details', name: 'Bank Account Details', required: true },
    { id: 'photo', name: 'Passport Size Photo', required: true },
    { id: 'address_proof', name: 'Address Proof', required: true },
    { id: 'other', name: 'Other Documents', required: false }
];

// Required fields for onboarding - Maps to EmployeeProfile fields
export const REQUIRED_FIELDS = {
    personalInfo: ['date_of_birth', 'gender', 'personal_email', 'phone'],
    addressInfo: ['address', 'city', 'state', 'country', 'postal_code'],
    bankInfo: ['bank_name', 'account_number', 'ifsc_code', 'account_holder_name'],
    emergencyContact: ['emergency_contact_name', 'emergency_contact_relation', 'emergency_contact_phone']
};

// Profile-to-Onboarding field mapping
// This ensures onboarding reads from employee profile data
export const PROFILE_FIELD_MAPPING = {
    // Personal Info fields from profile
    date_of_birth: 'date_of_birth',
    gender: 'gender',
    personal_email: 'personal_email',
    phone: 'phone',

    // Address fields from profile
    address: 'address',
    city: 'city',
    state: 'state',
    country: 'country',
    postal_code: 'postal_code',

    // Bank fields from profile
    bank_name: 'bank_name',
    account_number: 'account_number',
    ifsc_code: 'ifsc_code',
    account_holder_name: 'account_holder_name',

    // Emergency contact fields from profile
    emergency_contact_name: 'emergency_contact_name',
    emergency_contact_relation: 'emergency_contact_relation',
    emergency_contact_phone: 'emergency_contact_phone',

    // Employment fields from profile
    probation_period: 'probation_period_months'
};

export const OnboardingProvider = ({ children }) => {
    const { employees } = useEmployees();
    const { sendOnboardingNotification, addNotification } = useNotifications();
    const [onboardingData, setOnboardingData] = useState({});
    const [checklistConfig, setChecklistConfig] = useState({});
    const [configLoaded, setConfigLoaded] = useState(false); // Track if config has been fetched
    const [configVersion, setConfigVersion] = useState(0); // Track config changes to trigger re-calculation
    const [notificationsSent, setNotificationsSent] = useState(new Set());
    const [hiccupNotificationsSent, setHiccupNotificationsSent] = useState(new Set());
    const [loading, setLoading] = useState(false);

    // Debug: Log notification functions availability
    useEffect(() => {
        // //console.log('📋 OnboardingContext - Notification functions:', {
        //     hasSendOnboardingNotification: !!sendOnboardingNotification,
        //     hasAddNotification: !!addNotification
        // });
    }, [sendOnboardingNotification, addNotification]);

    // Fetch checklist config from backend
    const fetchChecklistConfig = async () => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        try {
            const config = await onboardingService.getChecklistConfig();
            setChecklistConfig(config || {});
            setConfigLoaded(true); // Mark as loaded even if empty
            setConfigVersion(prev => prev + 1); // Increment version to trigger re-calculation
            //console.log('✅ Checklist config loaded:', Object.keys(config || {}).length, 'items');
        } catch (error) {
            // Silently handle error to prevent console spam
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Checklist config fetch failed (non-critical):', error.message);
            }
            setConfigLoaded(false); // Failed to load
        }
    };

    // Listen for config changes from ChecklistConfiguration component
    useEffect(() => {
        const handleConfigChange = async () => {
            //console.log('🔄 Checklist config updated event received, reloading config...');
            await fetchChecklistConfig();
        };

        window.addEventListener('onboarding-config-updated', handleConfigChange);
        return () => window.removeEventListener('onboarding-config-updated', handleConfigChange);
    }, []);

    // Auto-create onboarding records for new employees (FIXED: only after initial data fetch)
    const [initialFetchDone, setInitialFetchDone] = useState(false);

    useEffect(() => {
        if (employees.length === 0 || loading || !initialFetchDone) return;

        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        employees.forEach(async (employee) => {
            // Only create if not in onboardingData from backend
            if (!onboardingData[employee.id]) {
                //console.log(`📝 Creating onboarding record for new employee: ${employee.name} (${employee.id})`);

                // Auto-populate fields from PROFILE data (Profile is source of truth)
                // Only include fields from PROFILE_FIELD_MAPPING
                const autoFilledFields = {};
                Object.entries(PROFILE_FIELD_MAPPING).forEach(([fieldKey, profileFieldName]) => {
                    const value = employee[profileFieldName];
                    if (value !== undefined && value !== null && value !== '') {
                        autoFilledFields[profileFieldName] = value;
                    }
                });

                try {
                    // Create in backend with profile-mapped fields
                    await onboardingService.upsert({
                        employeeId: employee.id,
                        status: 'pending',
                        fields: autoFilledFields
                        // adminReviewed: NOT sent - preserves existing value or defaults properly
                    });
                    //console.log(`✅ Created onboarding record for ${employee.name} (${employee.id})`);

                    // Refresh data from backend to get the created record
                    await fetchOnboardingData(true);
                } catch (error) {
                    console.error(`❌ Error creating onboarding record for ${employee.id}:`, error);
                }
            }
        });
    }, [employees, onboardingData, loading, initialFetchDone]);

    // Removed localStorage saving - data is synced with backend

    // DISABLED: Onboarding notifications are now sent directly from backend (onboarding.controller.js)
    // when tasks are completed. This prevents duplicate notifications.
    // The frontend just receives notifications via Socket.io real-time updates.
    // useEffect(() => {
    //     if (!sendOnboardingNotification) return;

    //     employees.forEach(employee => {
    //         const onboarding = onboardingData[employee.id];

    //         // Send notification only once when admin approves
    //         if (onboarding && onboarding.adminReviewed && !notificationsSent.has(employee.id)) {
    //             const employeeName = `${employee.first_name} ${employee.last_name}`;
    //             const designation = employee.designation || 'Employee';
    //             const legalEntity = employee.legal_entity || 'our organization';

    //             // Get manager ID from employee record
    //             const managerId = employee.manager_id || employee.managerId || employee.reporting_manager_id;

    //             // Send notification to employee and manager
    //             sendOnboardingNotification(employee.id, employeeName, designation, legalEntity, managerId);

    //             // Mark notification as sent
    //             setNotificationsSent(prev => new Set([...prev, employee.id]));

    //             //console.log(`✅ Onboarding notification sent to ${employeeName} (${employee.id})`);
    //             if (managerId) {
    //                 //console.log(`✅ Manager notification sent for ${employeeName} to manager (${managerId})`);
    //             }
    //         }
    //     });
    // }, [onboardingData, employees, sendOnboardingNotification, notificationsSent]);

    // Monitor for onboarding hiccups (approved employees who become incomplete)
    useEffect(() => {
        if (!addNotification) return;

        employees.forEach(employee => {
            const onboarding = onboardingData[employee.id];
            const percentage = getCompletionPercentage(employee.id);

            // Check if employee was previously approved but now has incomplete onboarding
            if (onboarding && onboarding.adminReviewed && percentage < 100) {
                const hiccupKey = `${employee.id}_${Date.now()}`;

                // Only send hiccup notification once per configuration change
                // We check if we've already sent a notification for this specific incomplete state
                if (!hiccupNotificationsSent.has(employee.id)) {
                    const employeeName = `${employee.first_name} ${employee.last_name}`;
                    const managerId = employee.manager_id || employee.managerId || employee.reporting_manager_id;

                    // Send notification to employee
                    addNotification({
                        userId: employee.id,
                        type: 'onboarding_hiccup',
                        title: 'Onboarding Update Required',
                        message: 'There is a hiccup! A few more information is required to complete your onboarding process.',
                        icon: 'AlertCircle',
                        color: 'orange'
                    });

                    // Send notification to manager
                    if (managerId) {
                        addNotification({
                            userId: managerId,
                            type: 'onboarding_hiccup_manager',
                            title: 'Team Member Onboarding Update',
                            message: `There is a hiccup! A few more information is required to complete ${employeeName}'s onboarding process.`,
                            icon: 'AlertCircle',
                            color: 'orange'
                        });
                    }

                    // Reset adminReviewed status to move back to pending
                    // Keep track of previous approval for admin notification
                    setOnboardingData(prev => ({
                        ...prev,
                        [employee.id]: {
                            ...prev[employee.id],
                            adminReviewed: false,
                            previouslyApproved: true, // Flag to show this was approved before
                            previousApprovalDate: prev[employee.id].reviewedAt,
                            status: 'in-progress',
                            updatedAt: new Date().toISOString()
                        }
                    }));

                    // Mark hiccup notification as sent
                    setHiccupNotificationsSent(prev => new Set([...prev, employee.id]));

                    //console.log(`⚠️ Onboarding hiccup notification sent to ${employeeName} (${employee.id})`);
                    if (managerId) {
                        //console.log(`⚠️ Manager hiccup notification sent for ${employeeName} to manager (${managerId})`);
                    }
                }
            } else if (onboarding && percentage === 100 && hiccupNotificationsSent.has(employee.id)) {
                // Remove from hiccup tracking when they reach 100% again
                setHiccupNotificationsSent(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(employee.id);
                    return newSet;
                });
            }
        });
    }, [onboardingData, employees, checklistConfig, addNotification, hiccupNotificationsSent]);

    // Update employee onboarding document
    const updateDocument = async (employeeId, documentId, documentData) => {
        const authToken = localStorage.getItem('authToken');

        // Optimistically update local state
        const updatedData = {
            ...documentData,
            uploadedAt: documentData?.uploadedAt || new Date().toISOString()
        };

        setOnboardingData(prev => {
            const employeeOnboarding = prev[employeeId] || {
                employeeId,
                status: 'in-progress',
                documents: {},
                fields: {},
                adminReviewed: false,
                createdAt: new Date().toISOString()
            };

            return {
                ...prev,
                [employeeId]: {
                    ...employeeOnboarding,
                    documents: {
                        ...employeeOnboarding.documents,
                        [documentId]: updatedData
                    },
                    fields: {
                        ...employeeOnboarding.fields,
                        [documentId]: updatedData
                    },
                    status: employeeOnboarding.status === 'pending' ? 'in-progress' : employeeOnboarding.status,
                    updatedAt: new Date().toISOString()
                }
            };
        });

        // Try to sync with backend
        if (authToken) {
            try {
                await onboardingService.uploadDocument(employeeId, {
                    documentId,
                    name: updatedData.name || documentId,
                    url: updatedData.filePath || updatedData.url,
                    type: updatedData.type,
                    size: updatedData.size,
                    filePath: updatedData.filePath,
                    downloadUrl: updatedData.url
                });

                await onboardingService.updateField(employeeId, documentId, updatedData);
            } catch (error) {
                console.error('Error syncing document to backend:', error);
            }
        }

        return updatedData;
    };

    // Update employee onboarding field
    const updateField = async (employeeId, fieldName, fieldValue) => {
        const authToken = localStorage.getItem('authToken');

        // Optimistically update local state
        setOnboardingData(prev => {
            const employeeOnboarding = prev[employeeId] || {
                employeeId,
                status: 'in-progress',
                documents: {},
                fields: {},
                adminReviewed: false,
                createdAt: new Date().toISOString()
            };

            return {
                ...prev,
                [employeeId]: {
                    ...employeeOnboarding,
                    fields: {
                        ...employeeOnboarding.fields,
                        [fieldName]: fieldValue
                    },
                    status: employeeOnboarding.status === 'pending' ? 'in-progress' : employeeOnboarding.status,
                    updatedAt: new Date().toISOString()
                }
            };
        });

        // Try to sync with backend
        if (authToken) {
            try {
                await onboardingService.updateField(employeeId, fieldName, fieldValue);
            } catch (error) {
                console.error('Error syncing field to backend:', error);
            }
        }
    };

    // Update multiple fields at once
    const updateFields = async (employeeId, fields) => {
        const authToken = localStorage.getItem('authToken');

        // Optimistically update local state
        setOnboardingData(prev => {
            const employeeOnboarding = prev[employeeId] || {
                employeeId,
                status: 'in-progress',
                documents: {},
                fields: {},
                adminReviewed: false,
                createdAt: new Date().toISOString()
            };

            return {
                ...prev,
                [employeeId]: {
                    ...employeeOnboarding,
                    fields: {
                        ...employeeOnboarding.fields,
                        ...fields
                    },
                    status: employeeOnboarding.status === 'pending' ? 'in-progress' : employeeOnboarding.status,
                    updatedAt: new Date().toISOString()
                }
            };
        });

        // Try to sync with backend
        if (authToken) {
            try {
                await onboardingService.updateFields(employeeId, fields);
            } catch (error) {
                console.error('Error syncing fields to backend:', error);
            }
        }
    };

    // Check if onboarding is complete
    const isOnboardingComplete = (employeeId) => {
        const onboarding = onboardingData[employeeId];
        if (!onboarding) return false;

        // Check required documents
        const requiredDocs = REQUIRED_DOCUMENTS.filter(doc => doc.required);
        const hasAllDocs = requiredDocs.every(doc =>
            onboarding.documents[doc.id] && onboarding.documents[doc.id].url
        );

        // Check required fields
        const allRequiredFields = [
            ...REQUIRED_FIELDS.personalInfo,
            ...REQUIRED_FIELDS.addressInfo,
            ...REQUIRED_FIELDS.bankInfo,
            ...REQUIRED_FIELDS.emergencyContact
        ];
        const hasAllFields = allRequiredFields.every(field =>
            onboarding.fields[field] && onboarding.fields[field].trim() !== ''
        );

        return hasAllDocs && hasAllFields && onboarding.adminReviewed;
    };

    // Admin review and approve
    const reviewAndApprove = async (employeeId, adminId, adminName) => {
        const authToken = localStorage.getItem('authToken');

        // Optimistic local update — flips the row to approved immediately so the
        // UI doesn't sit on a spinner waiting for fetchOnboardingData to settle.
        setOnboardingData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                status: 'completed',
                adminReviewed: true,
                previouslyApproved: false,
                previousApprovalDate: undefined,
                reviewedBy: adminName,
                reviewedById: adminId,
                reviewedAt: new Date().toISOString(),
                completedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            }
        }));

        if (!authToken) return { success: true };

        try {
            await onboardingService.reviewAndApprove(employeeId, true);
            // Refresh in the background — don't await so the UI doesn't block on it.
            fetchOnboardingData(true).catch((e) => console.warn('Post-approve refresh failed:', e?.message));
            return { success: true };
        } catch (error) {
            console.error('Error syncing approval to backend:', error);
            // Roll back the optimistic update so the row goes back to "ready" /
            // "in progress" instead of looking falsely approved.
            setOnboardingData(prev => ({
                ...prev,
                [employeeId]: {
                    ...prev[employeeId],
                    status: 'in-progress',
                    adminReviewed: false,
                    reviewedBy: undefined,
                    reviewedById: undefined,
                    reviewedAt: undefined,
                    completedAt: undefined,
                }
            }));
            return { success: false, error };
        }
    };

    /**
     * Request changes — sends the onboarding back to the employee so they can
     * edit and resubmit. Uses the same /approve endpoint with `approved: false`,
     * which the backend already handles (flips admin_reviewed=false, status='rejected').
     */
    const requestChanges = async (employeeId, comments, adminId, adminName) => {
        // Optimistic update so the row leaves the "ready" bucket immediately.
        setOnboardingData(prev => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                status: 'in-progress',
                adminReviewed: false,
                reviewerComments: comments || prev[employeeId]?.reviewerComments,
                reviewedBy: adminName,
                reviewedById: adminId,
                reviewedAt: new Date().toISOString(),
                completedAt: undefined,
                updatedAt: new Date().toISOString(),
            }
        }));

        const authToken = localStorage.getItem('authToken');
        if (!authToken) return { success: true };

        try {
            await onboardingService.reviewAndApprove(employeeId, false, comments);
            fetchOnboardingData(true).catch((e) => console.warn('Post-request-changes refresh failed:', e?.message));
            return { success: true };
        } catch (error) {
            console.error('Error requesting changes:', error);
            return { success: false, error };
        }
    };

    // Get onboarding completion percentage (respects admin configuration)
    // Status is based on PROFILE data completeness (Profile is source of truth)
    const getCompletionPercentage = (employeeId) => {
        const onboarding = onboardingData[employeeId];

        // If employee is admin-approved, always return 100%
        if (onboarding && onboarding.adminReviewed) {
            //console.log(`📊 Employee ${employeeId} is admin-approved, returning 100%`);
            return 100;
        }

        if (!onboarding) {
            //console.log(`❌ No onboarding data for employee ${employeeId}`);
            return 0;
        }

        // Get profile data (source of truth)
        const employeeProfile = onboarding.linkedEmployeeProfile ||
            employees.find(emp => emp.id === employeeId);

        // Get all checklist items
        const allItems = getAllChecklistItems();

        // Filter items based on configuration
        const requiredItems = allItems.filter(item => {
            const configStatus = checklistConfig[item.id];

            // If config has this item, use the config value
            if (configStatus !== undefined) {
                const isRequired = configStatus === 'required';
                return isRequired;
            }

            // If config is empty or item not in config, use the default from checklist
            const isRequired = item.required === true;
            return isRequired;
        });

        // If no required items (all items are optional or redundant)
        if (requiredItems.length === 0) {
            //console.log(`📈 No required items (all optional/redundant), returning 100%`);
            return 100;
        }

        // Count completed required items based on PROFILE DATA
        let completedRequired = 0;
        const completedItems = [];
        const missingItems = [];

        requiredItems.forEach(item => {
            let value = undefined;
            let isCompleted = false;

            // Check item in PROFILE first (mapped fields)
            if (employeeProfile && PROFILE_FIELD_MAPPING[item.id]) {
                const profileFieldName = PROFILE_FIELD_MAPPING[item.id];
                value = employeeProfile[profileFieldName];
            }

            // For file type items, also check documents storage (onboarding documents)
            if (item.type === 'file' && !value && onboarding.documents) {
                value = onboarding.documents[item.id];
            }

            // For regular fields, check onboarding fields as fallback
            if (!value && onboarding.fields) {
                value = onboarding.fields[item.id];
            }

            if (item.type === 'file') {
                // Check if file is uploaded
                isCompleted = value && value.url;
            } else if (item.type === 'checkbox' || item.type === 'signature') {
                // Check if checkbox/signature is checked
                isCompleted = value === true || value === 'true' || (value && value.url);
            } else {
                // Check if field is filled (from profile or onboarding)
                // Handle numeric values including 0, and empty strings
                if (value === null || value === undefined) {
                    isCompleted = false;
                } else if (typeof value === 'number') {
                    isCompleted = true; // 0 is a valid number
                } else if (typeof value === 'string') {
                    isCompleted = value.trim() !== '';
                } else {
                    isCompleted = Boolean(value);
                }
            }

            if (isCompleted) {
                completedRequired++;
                completedItems.push({ id: item.id, value: value, source: 'profile' });
            } else {
                missingItems.push({ id: item.id, value: value, type: item.type });
            }
        });

        //console.log(`✅ Completed: ${completedRequired}/${requiredItems.length}`, completedItems);
        //console.log(`❌ Missing: ${missingItems.length}`, missingItems);

        // Calculate percentage based only on required items
        const totalRequired = requiredItems.length;
        const totalCompleted = completedRequired;
        const percentage = Math.round((totalCompleted / totalRequired) * 100);

        //console.log(`📈 Progress: ${percentage}%`);

        return percentage;
    };

    // Get employees by onboarding status
    const getEmployeesByStatus = (status) => {
        return employees.filter(emp => {
            const onboarding = onboardingData[emp.id];
            return onboarding && onboarding.status === status;
        });
    };

    // Update employee status based on completion percentage
    const updateEmployeeStatus = (employeeId) => {
        const percentage = getCompletionPercentage(employeeId);
        const onboarding = onboardingData[employeeId];

        if (!onboarding) return;

        let newStatus = onboarding.status;

        // Auto-update status based on progress
        if (percentage === 100 && !onboarding.adminReviewed) {
            newStatus = 'completed'; // Ready for admin review
        } else if (percentage > 0 && percentage < 100) {
            newStatus = 'in-progress';
        } else if (percentage === 0) {
            newStatus = 'pending';
        }

        // Only update if status changed
        if (newStatus !== onboarding.status) {
            setOnboardingData(prev => ({
                ...prev,
                [employeeId]: {
                    ...prev[employeeId],
                    status: newStatus,
                    updatedAt: new Date().toISOString()
                }
            }));
        }
    };

    // Get pending onboarding employees
    const getPendingOnboarding = () => {
        return employees.filter(emp => {
            const onboarding = onboardingData[emp.id];
            if (!onboarding) return true;

            // Use completion percentage + admin review to match card display logic
            const pct = getCompletionPercentage(emp.id);
            return !(pct === 100 && onboarding.adminReviewed);
        });
    };

    // Get completed onboarding employees
    const getCompletedOnboarding = () => {
        return employees.filter(emp => {
            const onboarding = onboardingData[emp.id];
            if (!onboarding) return false;

            // Match card display logic: 100% complete + admin reviewed = completed
            const pct = getCompletionPercentage(emp.id);
            return pct === 100 && onboarding.adminReviewed === true;
        });
    };

    // Get visible items for employee (excludes redundant items)
    const getVisibleChecklistItems = () => {
        const allItems = getAllChecklistItems();
        return allItems.filter(item => {
            const configStatus = checklistConfig[item.id];
            return configStatus !== 'redundant';
        });
    };

    // Get item status from configuration
    const getItemStatus = (itemId, defaultRequired) => {
        const configStatus = checklistConfig[itemId];
        if (configStatus) return configStatus;
        return defaultRequired ? 'required' : 'optional';
    };

    // Fetch onboarding data from backend and merge with profile data
    // Profile is the source of truth for field values
    const fetchOnboardingData = async (forceRefresh = false) => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            // Silently return if no auth token
            return;
        }

        try {
            setLoading(true);
            const data = await onboardingService.getAll();

            // Convert array to object keyed by employee_id
            const onboardingMap = {};
            data.forEach(record => {
                // Find corresponding employee from EmployeeContext
                const employeeProfile = employees.find(emp => emp.id === record.employee_id);

                // Merge profile data into onboarding fields
                // Profile is the source of truth
                const profileFields = {};
                if (employeeProfile) {
                    Object.entries(PROFILE_FIELD_MAPPING).forEach(([fieldKey, fieldName]) => {
                        const value = employeeProfile[fieldName];
                        if (value !== undefined && value !== null && value !== '') {
                            profileFields[fieldName] = value;
                        }
                    });
                }

                onboardingMap[record.employee_id] = {
                    employeeId: record.employee_id,
                    status: record.status,
                    documents: record.documents || {},
                    // Merge backend fields with profile data (profile data takes precedence)
                    fields: {
                        ...record.fields,
                        ...profileFields
                    },
                    adminReviewed: record.admin_reviewed,
                    reviewedBy: record.reviewed_by_name || record.reviewed_by,
                    reviewedAt: record.reviewed_at,
                    completedAt: record.completed_at,
                    createdAt: record.created_at,
                    updatedAt: record.updated_at,
                    // Reference to profile for real-time sync
                    linkedEmployeeProfile: employeeProfile
                };
            });

            setOnboardingData(onboardingMap);
        } catch (error) {
            // Silently handle error to prevent console spam
            // Keep existing data if fetch fails
            if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ Onboarding data fetch failed (non-critical):', error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch fresh onboarding data from backend after initial load
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
            fetchOnboardingData().then(() => {
                setInitialFetchDone(true);
                //console.log('✅ Initial onboarding data fetch completed');
            });
            fetchChecklistConfig();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const value = {
        onboardingData,
        checklistConfig,
        loading,
        fetchOnboardingData,
        fetchChecklistConfig,
        updateDocument,
        updateField,
        updateFields,
        isOnboardingComplete,
        reviewAndApprove,
        requestChanges,
        getCompletionPercentage,
        getEmployeesByStatus,
        getPendingOnboarding,
        getCompletedOnboarding,
        getVisibleChecklistItems,
        getItemStatus,
        REQUIRED_DOCUMENTS,
        REQUIRED_FIELDS
    };

    return (
        <OnboardingContext.Provider value={value}>
            {children}
        </OnboardingContext.Provider>
    );
};
